import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import {
  Play,
  Square,
  RefreshCw,
  Bot,
  ChevronRight,
  ChevronDown,
  ArrowRightToLine,
} from "lucide-react";
import { toast } from "sonner";
import { LogTerminal, LogTerminalRef } from "@/shared/components/ui/log-terminal";
import {
  getEngineAgentExecutions,
  type AgentExecutionFeedItem,
} from "../services/serviceLogsService";

// Agent-execution feed (Engine tab → "Agents" mode, extensão do PBI 81485).
// Mesmo modelo das outras abas: sidebar de agentes (sessões), acordeão por agente
// no "Todos", visão dedicada por agente e expansão de detalhes por execução.

const POLL_MS = 5000;
const FEED_TAIL = 200;

// Status são semânticos, então vêm dos tokens do contrato — ao contrário das
// cores de canal/tipo, que são identidade e seguem fixas de propósito.
const STATUS_CLASSES: Record<string, string> = {
  completed: "text-success-fg bg-success-bg",
  failed: "text-error-fg bg-error-bg",
  running: "text-info-fg bg-info-bg",
  pending: "text-warning-fg bg-warning-bg",
};

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// Compact range: same day → "8/11/2026, 10:01 → 10:37"; cross-day shows both dates.
const formatRange = (firstIso: string, lastIso: string): string => {
  const first = new Date(firstIso);
  const last = new Date(lastIso);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return formatTime(lastIso);
  if (first.getTime() === last.getTime()) return last.toLocaleString();
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if (first.toDateString() === last.toDateString()) {
    return `${first.toLocaleDateString()}, ${first.toLocaleTimeString([], timeOpts)} → ${last.toLocaleTimeString([], timeOpts)}`;
  }
  return `${first.toLocaleString()} → ${last.toLocaleString()}`;
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

interface AgentGroup {
  sessionKey: string;
  agentName: string;
  organizationName: string | null;
  productName: string | null;
  firstAt: string;
  lastAt: string;
  count: number;
  failedCount: number;
}

// Groups feed items (newest first) into agent blocks ordered by latest activity.
const groupAgents = (items: AgentExecutionFeedItem[]): AgentGroup[] => {
  const groups = new Map<string, AgentGroup>();
  for (const item of items) {
    const failed = item.status === "failed" ? 1 : 0;
    const existing = groups.get(item.sessionKey);
    if (existing) {
      existing.count += 1;
      existing.failedCount += failed;
      // itens chegam do mais novo para o mais antigo — o último visto é o firstAt
      existing.firstAt = item.timestamp;
    } else {
      groups.set(item.sessionKey, {
        sessionKey: item.sessionKey,
        agentName: item.agentName,
        organizationName: item.organizationName,
        productName: item.productName,
        firstAt: item.timestamp,
        lastAt: item.timestamp,
        count: 1,
        failedCount: failed,
      });
    }
  }
  return [...groups.values()];
};

export const EngineAgentsFeed = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<AgentExecutionFeedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState<string>(""); // '' = live
  const [selectedSession, setSelectedSession] = useState<string | null>(null); // null = all
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Live-poll health: silent failures must not leave a green "live" badge while
  // the data is actually stale — surfaced as a non-intrusive indicator.
  const [pollFailed, setPollFailed] = useState(false);
  const logTerminalRef = useRef<LogTerminalRef>(null);
  const isPausedRef = useRef(isPaused);
  // Monotonic request sequence: a slow older response must not overwrite the
  // state written by a newer one (e.g. day switch racing the live poll).
  const requestSeqRef = useRef(0);

  const isLive = date === "";

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const load = useCallback(
    async (targetDate: string, silent = false) => {
      const requestSeq = ++requestSeqRef.current;
      if (!silent) setIsLoading(true);
      try {
        const response = await getEngineAgentExecutions({
          date: targetDate || null,
          tail: FEED_TAIL,
        });
        // Every state update derived from this request stays behind the sequence
        // guard — a stale success must not clear a newer failure (and vice versa).
        if (requestSeq === requestSeqRef.current) {
          setItems(response.items ?? []);
          setPollFailed(false);
        }
      } catch {
        if (requestSeq === requestSeqRef.current) setPollFailed(true);
        if (!silent)
          toast.error(t("logs.agents.loadError", "Could not load the workflow-execution feed."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [t]
  );

  // Live mode: serialized recursive setTimeout (never overlapping requests).
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (!isPausedRef.current) await load("", true);
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    // finally (não then): um erro no load inicial não pode matar o polling —
    // o load já trata o erro; aqui só garantimos que o loop continue.
    void load(date).finally(() => {
      if (isLive && !cancelled) timer = setTimeout(tick, POLL_MS);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [date, isLive, load]);

  // Reset selection when the day changes (agent may not exist there).
  useEffect(() => {
    setSelectedSession(null);
    setExpandedIds(new Set());
    setExpandedGroups(new Set());
  }, [date]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (sessionKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(sessionKey)) next.delete(sessionKey);
      else next.add(sessionKey);
      return next;
    });
  };

  const sessions = useMemo(() => groupAgents(items), [items]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedSession) {
      result = result.filter((item) => item.sessionKey === selectedSession);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.agentName.toLowerCase().includes(query) ||
          (item.organizationName ?? "").toLowerCase().includes(query) ||
          (item.productName ?? "").toLowerCase().includes(query) ||
          item.status.toLowerCase().includes(query) ||
          (item.errorMessage ?? "").toLowerCase().includes(query)
      );
    }
    return result;
  }, [items, selectedSession, searchQuery]);

  const isSessionView = selectedSession !== null;

  const sessionItems = useMemo(
    () => (isSessionView ? [...filteredItems].reverse() : filteredItems),
    [isSessionView, filteredItems]
  );

  const groupedFeed = useMemo(() => {
    if (isSessionView) return [];
    const map = new Map<string, AgentExecutionFeedItem[]>();
    for (const item of filteredItems) {
      const arr = map.get(item.sessionKey) ?? [];
      arr.push(item);
      map.set(item.sessionKey, arr);
    }
    return [...map.entries()].map(([sessionKey, groupItems]) => ({
      sessionKey,
      items: [...groupItems].reverse(),
    }));
  }, [isSessionView, filteredItems]);

  // Abrir um agente é navegação explícita: força o scroll mesmo que o usuário
  // tenha rolado para cima na visão anterior (senão a visão abre no meio).
  useEffect(() => {
    if (!isSessionView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.forceScrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isSessionView, selectedSession]);

  // Execuções novas do agente aberto acompanham o fim apenas se o usuário não
  // tiver rolado para cima para ler algo.
  useEffect(() => {
    if (!isSessionView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.scrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isSessionView, sessionItems.length]);

  const renderSessionBlock = (session: AgentGroup) => {
    const isSelected = selectedSession === session.sessionKey;
    return (
      <button
        key={session.sessionKey}
        onClick={() => setSelectedSession(isSelected ? null : session.sessionKey)}
        className={`w-full text-left rounded-md border p-2 space-y-1 transition-colors ${
          isSelected ? "border-primary bg-primary/10" : "border-border hover:bg-muted/60"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium truncate">{session.agentName}</span>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            {session.count} {t("logs.agents.executions", "execuções")}
            {session.failedCount > 0 && (
              <span className="text-red-400 ml-1">
                · {session.failedCount} {t("logs.tools.failed", "falhas")}
              </span>
            )}
          </span>
        </div>
        {session.organizationName && (
          <div className="text-xs text-muted-foreground truncate">{session.organizationName}</div>
        )}
        {session.productName && (
          <div className="text-xs text-muted-foreground truncate">{session.productName}</div>
        )}
        <div className="text-[10px] text-muted-foreground">
          {formatRange(session.firstAt, session.lastAt)}
        </div>
      </button>
    );
  };

  // Linha de execução — contraído = só a linha; expandido = detalhes indentados.
  const renderExecutionRow = (item: AgentExecutionFeedItem) => {
    const statusClass = STATUS_CLASSES[item.status] ?? "text-gray-400 bg-gray-400/10";
    const details: string[] = [];
    if (item.durationMs !== null)
      details.push(`${t("logs.agents.duration", "Duração")}: ${formatDuration(item.durationMs)}`);
    if (item.steps !== null) details.push(`${t("logs.agents.steps", "Steps")}: ${item.steps}`);
    if (item.totalTokens !== null)
      details.push(`${t("logs.agents.tokens", "Tokens")}: ${item.totalTokens}`);
    const hasDetails = details.length > 0 || !!item.errorMessage;
    const isExpanded = expandedIds.has(item.id);
    return (
      <div key={item.id} className="py-0.5">
        <button
          onClick={() => hasDetails && toggleExpanded(item.id)}
          className={`flex items-center gap-2 flex-wrap text-xs w-full text-left ${
            hasDetails ? "" : "cursor-default"
          }`}
          title={
            hasDetails
              ? isExpanded
                ? t("logs.chats.collapse", "Contrair")
                : t("logs.chats.expand", "Expandir")
              : undefined
          }
        >
          {hasDetails ? (
            <span className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0">
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </span>
          ) : (
            <span className="w-3.5 shrink-0" />
          )}
          <span className="text-gray-500">[{formatTime(item.timestamp)}]</span>
          <span className="text-emerald-600 dark:text-emerald-300 font-mono font-medium">
            {item.agentName}
          </span>
          <span
            className={`${statusClass} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
          >
            {item.status}
          </span>
          {item.durationMs !== null && (
            <span className="text-gray-500 text-[10px]">{formatDuration(item.durationMs)}</span>
          )}
        </button>
        {isExpanded && (
          <div className="mt-1 ml-5 border-l border-hairline pl-3 space-y-1">
            {details.length > 0 && (
              <div className="text-muted-foreground text-xs">{details.join(" · ")}</div>
            )}
            {item.errorMessage && (
              <div className="text-red-500 dark:text-red-400 text-xs whitespace-pre-wrap break-words">
                {item.errorMessage}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder={t("logs.agents.searchPlaceholder", "Buscar nas execuções de agentes...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 max-w-md min-w-[180px]"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {t("logs.dateSelector", "Data")}:
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 text-sm px-3 rounded-md border border-input bg-background"
          />
          {!isLive && (
            <button onClick={() => setDate("")} className="text-sm text-primary hover:underline">
              {t("logs.liveOption", "Tempo real (hoje)")}
            </button>
          )}
        </div>
        <span className="text-sm">
          {isLive ? (
            isPaused ? (
              <span className="flex items-center text-yellow-500">
                <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></span>
                {t("logs.paused", "Pausado")}
              </span>
            ) : pollFailed ? (
              <span className="flex items-center text-red-500">
                <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                {t("logs.liveUpdatesFailing", "Tempo real com falhas — dados podem estar desatualizados")}
              </span>
            ) : (
              <span className="flex items-center text-green-500">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                {t("logs.agents.liveFeed", "Feed em tempo real")}
              </span>
            )
          ) : (
            <span className="flex items-center text-blue-400">
              <span className="h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
              {t("logs.historicalSnapshot", "Snapshot histórico — sem atualização em tempo real")}
            </span>
          )}
        </span>
        {filteredItems.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {t("logs.agents.itemCount", { count: filteredItems.length })}
          </span>
        )}
      </div>

      <div className="flex gap-4 h-[500px]">
        {/* Agent sidebar */}
        <div className="w-60 shrink-0 flex flex-col border rounded-md bg-card overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center gap-2 text-sm font-medium">
            <Bot className="h-4 w-4" />
            {t("logs.agents.sessions", "Agentes")}
            <span className="ml-auto text-xs text-muted-foreground">{sessions.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <button
              onClick={() => setSelectedSession(null)}
              className={`w-full text-left rounded-md border p-2 text-sm transition-colors ${
                selectedSession === null
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border hover:bg-muted/60"
              }`}
            >
              {t("logs.agents.allSessions", "Todos os agentes")}
            </button>
            {sessions.map(renderSessionBlock)}
            {sessions.length === 0 && !isLoading && (
              <p className="text-xs text-muted-foreground px-1 py-2">
                {t("logs.agents.noSessions", "Nenhuma execução no período.")}
              </p>
            )}
          </div>
        </div>

        {/* Execution feed */}
        <div className="flex-1 overflow-hidden">
          {filteredItems.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
              <p>{t("logs.agents.empty", "Nenhuma execução no período selecionado.")}</p>
            </div>
          ) : (
            <LogTerminal
              className="h-full"
              ref={logTerminalRef}
              title={t("logs.agents.terminalTitle", "Execuções de agentes (Engine)")}
              actionButtons={
                <div className="flex space-x-3">
                  {isLive &&
                    (isPaused ? (
                      <button
                        onClick={() => setIsPaused(false)}
                        className="text-green-500 hover:text-green-700 focus:outline-none"
                        title={t("logs.resume", "Retomar")}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsPaused(true)}
                        className="text-yellow-500 hover:text-yellow-700 focus:outline-none"
                        title={t("logs.pause", "Pausar")}
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    ))}
                  <button
                    onClick={() => load(date)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    title={t("logs.agents.refresh", "Atualizar")}
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              }
            >
              {isSessionView
                ? // Agente selecionado — execuções em ordem cronológica.
                  sessionItems.map((item) => renderExecutionRow(item))
                : // "Todos os agentes" — acordeão por agente.
                  groupedFeed.map((group) => {
                    const head = group.items[group.items.length - 1]; // mais recente
                    const first = group.items[0];
                    const failedCount = group.items.filter((i) => i.status === "failed").length;
                    const groupExpanded = expandedGroups.has(group.sessionKey);
                    return (
                      <div
                        key={group.sessionKey}
                        className="py-2 border-b border-hairline last:border-b-0"
                      >
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleGroup(group.sessionKey)}
                            className="flex items-center gap-2 flex-wrap text-xs flex-1 min-w-0 text-left hover:bg-gray-200/40 dark:hover:bg-white/5 rounded px-1 py-0.5"
                            title={
                              groupExpanded
                                ? t("logs.chats.collapse", "Contrair")
                                : t("logs.chats.expand", "Expandir")
                            }
                          >
                            <span className="text-gray-500 shrink-0">
                              {groupExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </span>
                            <span className="text-emerald-600 dark:text-emerald-300 font-mono font-medium">
                              {head.agentName}
                            </span>
                            {head.organizationName && (
                              <span className="text-sky-600 dark:text-sky-400">
                                {head.organizationName}
                              </span>
                            )}
                            {head.productName && (
                              <span className="text-teal-600 dark:text-teal-400">
                                {head.productName}
                              </span>
                            )}
                            <span className="ml-auto text-gray-500 text-[10px] whitespace-nowrap">
                              {formatRange(first.timestamp, head.timestamp)} · {group.items.length}{" "}
                              {t("logs.agents.executions", "execuções")}
                              {failedCount > 0 && (
                                <span className="text-red-400">
                                  {" "}
                                  · {failedCount} {t("logs.tools.failed", "falhas")}
                                </span>
                              )}
                            </span>
                          </button>
                          <button
                            onClick={() => setSelectedSession(group.sessionKey)}
                            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none shrink-0 px-1"
                            title={t("logs.agents.openSession", "Abrir agente")}
                          >
                            <ArrowRightToLine className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {groupExpanded && (
                          <div className="mt-1 ml-5 border-l border-hairline pl-2 space-y-1">
                            {group.items.map((item) => renderExecutionRow(item))}
                          </div>
                        )}
                      </div>
                    );
                  })}
            </LogTerminal>
          )}
        </div>
      </div>
    </div>
  );
};
